import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Shield, Clock, User, MessageSquare, Smartphone } from 'lucide-react';
import axios from 'axios';

const SignatureDisplay = ({ serviceLogId, signatures = [], onVerifySignature }) => {
  const [verificationResults, setVerificationResults] = useState({});
  const [loading, setLoading] = useState({});
  const [expandedSignature, setExpandedSignature] = useState(null);

  const getSignatureTypeIcon = (type) => {
    switch (type) {
      case 'technician':
        return <User className="h-4 w-4" />;
      case 'approval':
        return <CheckCircle className="h-4 w-4" />;
      case 'witness':
        return <Eye className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getSignatureTypeLabel = (type) => {
    switch (type) {
      case 'technician':
        return 'Technician';
      case 'approval':
        return 'Approval';
      case 'witness':
        return 'Witness';
      default:
        return 'Unknown';
    }
  };

  const getSignatureTypeColor = (type) => {
    switch (type) {
      case 'technician':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'approval':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'witness':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const verifySignature = async (signatureId) => {
    if (loading[signatureId]) return;
    
    setLoading(prev => ({ ...prev, [signatureId]: true }));
    
    try {
      const response = await axios.get(
        `/api/service-logs/${serviceLogId}/signatures/${signatureId}/verify`
      );
      
      setVerificationResults(prev => ({
        ...prev,
        [signatureId]: response.data
      }));
      
      if (onVerifySignature) {
        onVerifySignature(signatureId, response.data);
      }
    } catch (error) {
      console.error('Error verifying signature:', error);
      setVerificationResults(prev => ({
        ...prev,
        [signatureId]: {
          is_valid: false,
          verification_details: {
            error: error.response?.data?.detail || 'Verification failed'
          }
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [signatureId]: false }));
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const toggleSignatureDetails = (signatureId) => {
    setExpandedSignature(expandedSignature === signatureId ? null : signatureId);
  };

  if (!signatures || signatures.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Signatures</h3>
        <p className="text-gray-600">This service log has not been signed yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Digital Signatures ({signatures.length})
        </h3>
      </div>

      <div className="space-y-3">
        {signatures.map((signature) => {
          const verification = verificationResults[signature.id];
          const isExpanded = expandedSignature === signature.id;
          
          return (
            <div
              key={signature.id}
              className="border border-gray-200 rounded-lg bg-white shadow-sm"
            >
              {/* Signature Header */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                      getSignatureTypeColor(signature.signature_type)
                    }`}>
                      {getSignatureTypeIcon(signature.signature_type)}
                      <span className="ml-1">{getSignatureTypeLabel(signature.signature_type)}</span>
                    </div>
                    
                    <div>
                      <p className="font-medium text-gray-900">{signature.signer_name}</p>
                      <p className="text-sm text-gray-600">{signature.signer_role}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Verification Status */}
                    {verification && (
                      <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        verification.is_valid 
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {verification.is_valid ? (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1" />
                        )}
                        {verification.is_valid ? 'Verified' : 'Invalid'}
                      </div>
                    )}
                    
                    {/* Verify Button */}
                    <button
                      onClick={() => verifySignature(signature.id)}
                      disabled={loading[signature.id]}
                      className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading[signature.id] ? 'Verifying...' : 'Verify'}
                    </button>
                    
                    {/* Expand Button */}
                    <button
                      onClick={() => toggleSignatureDetails(signature.id)}
                      className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 transition-colors"
                    >
                      {isExpanded ? 'Hide Details' : 'Show Details'}
                    </button>
                  </div>
                </div>
                
                {/* Timestamp */}
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4 mr-1" />
                  Signed on {formatTimestamp(signature.timestamp)}
                </div>
              </div>
              
              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="p-4 space-y-4">
                    {/* Signature Image */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Signature
                      </label>
                      <div className="border border-gray-200 rounded bg-white p-2">
                        <img
                          src={signature.signature_data}
                          alt="Digital Signature"
                          className="max-w-full h-auto max-h-32"
                        />
                      </div>
                    </div>
                    
                    {/* Comments */}
                    {signature.comments && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MessageSquare className="h-4 w-4 inline mr-1" />
                          Comments
                        </label>
                        <div className="bg-white border border-gray-200 rounded p-3 text-sm text-gray-700">
                          {signature.comments}
                        </div>
                      </div>
                    )}
                    
                    {/* Technical Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Signer Information
                        </label>
                        <div className="bg-white border border-gray-200 rounded p-3 text-xs text-gray-600 space-y-1">
                          <div><strong>Email:</strong> {signature.signer_email}</div>
                          <div><strong>Role:</strong> {signature.signer_role}</div>
                          <div><strong>IP Address:</strong> {signature.ip_address || 'Not recorded'}</div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Smartphone className="h-4 w-4 inline mr-1" />
                          Device Information
                        </label>
                        <div className="bg-white border border-gray-200 rounded p-3 text-xs text-gray-600">
                          {signature.device_info || 'Not recorded'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Verification Details */}
                    {verification && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Verification Results
                        </label>
                        <div className="bg-white border border-gray-200 rounded p-3">
                          <div className="space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span>Token Validity:</span>
                              <span className={verification.verification_details.token_valid ? 'text-green-600' : 'text-red-600'}>
                                {verification.verification_details.token_valid ? '✓ Valid' : '✗ Invalid'}
                              </span>
                            </div>
                            
                            {verification.verification_details.document_integrity !== undefined && (
                              <div className="flex items-center justify-between">
                                <span>Document Integrity:</span>
                                <span className={verification.verification_details.document_integrity ? 'text-green-600' : 'text-red-600'}>
                                  {verification.verification_details.document_integrity ? '✓ Intact' : '✗ Modified'}
                                </span>
                              </div>
                            )}
                            
                            {verification.verification_details.chain_integrity !== undefined && (
                              <div className="flex items-center justify-between">
                                <span>Chain Integrity:</span>
                                <span className={verification.verification_details.chain_integrity ? 'text-green-600' : 'text-red-600'}>
                                  {verification.verification_details.chain_integrity ? '✓ Valid' : '✗ Compromised'}
                                </span>
                              </div>
                            )}
                            
                            {verification.verification_details.error && (
                              <div className="text-red-600">
                                <strong>Error:</strong> {verification.verification_details.error}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Cryptographic Hash */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cryptographic Hash
                      </label>
                      <div className="bg-white border border-gray-200 rounded p-3 text-xs text-gray-600 font-mono break-all">
                        {signature.signature_hash}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SignatureDisplay;